import { Action, Selector, State, StateContext } from "@ngxs/store";
import { DiaryStateModel } from "./diary-model";
import { Injectable } from "@angular/core";
import { DiaryService } from "../../services/diary.service";
import { Diaries } from "./diary-actions";
import { append, patch, removeItem, updateItem } from "@ngxs/store/operators";
import { plainToInstance } from "class-transformer";
import { Diary } from "../../models/diary"
import { UserService } from "../../services/user.service"
import { lastValueFrom, map } from "rxjs";
import { Users } from "../user/user-actions";


@State<DiaryStateModel>
    (
        {
            name: "diaries",
            defaults:
            {
                openDiaries: [],
                closedDiaries: [],
                closedDateLookbackDays: 30,
                dueDateLookaheadDays: 30

            }
        }
    )

@Injectable()
export class DiaryState {
    constructor
        (
            private _diaryService: DiaryService,
            private _userService: UserService
        ) {

    }

    @Selector()
    static openDiaries(state: DiaryStateModel) {
        return [...state.openDiaries.map(d => plainToInstance(Diary, d))];
    }

    @Selector()
    static closedDiaries(state: DiaryStateModel) {
        return [...state.closedDiaries.map(d => plainToInstance(Diary, d))];
    }

    @Selector()
    static closedDateLookbackDays(state: DiaryStateModel) {
        return state.closedDateLookbackDays;
    }

    @Selector()
    static dueDateLookaheadDays(state: DiaryStateModel) {
        return state.dueDateLookaheadDays;
    }

    @Action([Diaries.DiaryManagerLookaheadChanged])
    lookaheadChanged(ctx: StateContext<DiaryStateModel>, action: Diaries.DiaryManagerLookaheadChanged) {
        ctx.patchState
            (
                { dueDateLookaheadDays: action.payload.lookahead }
            )
        return ctx.dispatch(new Diaries.RefreshOpenDiaries());
    }

    @Action([Diaries.DiaryManagerLookbackChanged])
    lookbackChanged(ctx: StateContext<DiaryStateModel>, action: Diaries.DiaryManagerLookbackChanged) {
        ctx.patchState
            (
                { closedDateLookbackDays: action.payload.lookback }
            )
        return ctx.dispatch(new Diaries.RefreshClosedDiaries());
    }


    @Action([Diaries.RefreshClosedDiaries])
    refreshClosedDiaries(ctx: StateContext<DiaryStateModel>, action: Diaries.RefreshClosedDiaries) {
        var state = ctx.getState();
        //Get Open Diaries For User
        var closedFrom = new Date(new Date().valueOf() - state.closedDateLookbackDays * 86400000)


        return this._diaryService.getMyClosedDiaries(closedFrom, undefined)
            .pipe
            (
                map
                    (
                        d => ctx.setState(patch({ closedDiaries: d }))
                    )
            );
    }


    @Action([Diaries.RefreshOpenDiaries])
    refreshOpenDiaries(ctx: StateContext<DiaryStateModel>, action: Diaries.RefreshOpenDiaries) {
        var state = ctx.getState();

        var dueTo = new Date(new Date().valueOf() + state.dueDateLookaheadDays * 86400000)

        return this._diaryService.getMyOpenDiaries(undefined, dueTo)
            .pipe
            (
                map
                    (
                        d => ctx.setState(patch({ openDiaries: d }))
                    )
            );
    }

    @Action([Diaries.EncounterServiceDiaryUpdated])
    async diaryUpdated(ctx: StateContext<DiaryStateModel>, action: Diaries.EncounterServiceDiaryUpdated) {
        var state = ctx.getState();

        if (state.openDiaries.map(d => d.id).includes(action.payload.diaryId)) {
            ctx.setState
                (
                    patch
                        (
                            {
                                openDiaries: updateItem(d => d.id === action.payload.diaryId, action.payload.diary)
                            }
                        )
                )
        }

        if (state.closedDiaries.map(d => d.id).includes(action.payload.diaryId)) {
            ctx.setState
                (
                    patch
                        (
                            {
                                closedDiaries: updateItem(d => d.id === action.payload.diaryId, action.payload.diary)
                            }
                        )
                )
        }
    }

    @Action([Diaries.EncounterServiceDiaryClosed])
    async diaryClosed(ctx: StateContext<DiaryStateModel>, action: Diaries.EncounterServiceDiaryClosed) {
        //If this diary exists in the open list, move it to the closed list
        var state = ctx.getState();

        if (state.openDiaries.map(d => d.id).includes(action.payload.diaryId)) {
            ctx.setState
                (
                    patch
                        (
                            {
                                openDiaries: removeItem(d => d.id === action.payload.diaryId)
                            }
                        )
                )
        }

        if (!state.closedDiaries.map(d => d.id).includes(action.payload.diaryId)) {
            this._diaryService.getDiary(action.payload.diaryId)
                .pipe
                (
                    map
                        (
                            d => {
                                if (d) {
                                    ctx.setState
                                        (
                                            patch
                                                (
                                                    {
                                                        closedDiaries: append([d])
                                                    }
                                                )
                                        )
                                }
                            }
                        )
                ).subscribe();
        }
    }

    @Action([Diaries.DiaryServiceDiaryAssigned, Diaries.EncounterServiceDiaryCreated, Diaries.EncounterServiceDiaryAssigned])
    async diaryAdded(ctx: StateContext<DiaryStateModel>, action: Diaries.DiaryServiceDiaryAssigned | Diaries.EncounterServiceDiaryCreated | Diaries.EncounterServiceDiaryAssigned) {
        var state = ctx.getState();
        //If this diary is one of 'mine', then 1.) Add it to the list of open diaries (assuming it's not also closed)
        //and 2.) 
        var me =
            await lastValueFrom(this._userService.getMe());
        if (action instanceof Diaries.EncounterServiceDiaryCreated) {
            if (action.payload.diary.assignedTo?.id != me.id)
                return;
        }
        else {
            if (action.payload.userId != me.id)
                return;
        }

        //If here, we own this. Add it to our list

        if (!state.openDiaries.map(s => s.id).includes(action.payload.diaryId)) {
            //Get the diary
            this._diaryService.getDiary(action.payload.diaryId)
                .pipe
                (
                    map
                        (
                            d => {
                                if (d) {
                                    ctx.setState(patch({ openDiaries: append([d]) }))
                                }
                            }
                        )
                ).subscribe();
        }
    }

}